/**
 * "Values that did not match" for step 2 (f10.5, design D10 section 5): one row
 * per country column that resolved to no country, with a *Map to* select (the
 * countries already carried by another column disabled) and *Exclude*.
 *
 * Every rule -- who may be mapped, conflicts, versions -- is the server's; the
 * controls render only when `capabilities.can_decide` says so.
 */

import { useState } from 'react';
import { Ban, Check, RotateCcw } from 'lucide-react';

import type { BillableDaysCountryItem, BillableDaysReview } from '@/api/client';
import { useDecideBillableDaysCountry } from '@/api/queries';

import { MATCHED, UNDECIDED, decisionPill, refusalMessage } from './billableDaysView';

export function UnmatchedCountriesCard({ cycleId, review }: { cycleId: string; review: BillableDaysReview }) {
  const decide = useDecideBillableDaysCountry(cycleId, review.batch.id);
  const unmatched = review.countries.filter((item) => item.decision.code !== MATCHED);
  const undecided = unmatched.filter((item) => item.decision.code === UNDECIDED).length;
  const canDecide = review.capabilities.can_decide;

  // Countries a column already lands on: the matched ones and every MAP.
  const taken = new Map<string, string>();
  for (const item of review.countries) {
    const target = item.decided_country ?? item.country;
    if (target && item.decision.code !== 'EXCLUDE' && item.decision.code !== UNDECIDED) {
      taken.set(target.id, item.id);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Values that did not match</h3>
        {undecided > 0 ? (
          <span className="badge pill-warning">{undecided} awaiting decision</span>
        ) : (
          <span className="badge pill-success">All decided</span>
        )}
      </div>
      <div className="card-body stack">
        {decide.error ? (
          <div className="alert alert-danger" role="alert">
            <div>
              <div className="a-body">{refusalMessage(decide.error)}</div>
            </div>
          </div>
        ) : null}

        {unmatched.length === 0 ? (
          <p className="hint">All {review.countries.length} countries matched</p>
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Column in the report</th>
                  <th>Decision</th>
                  <th>Map to</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {unmatched.map((item) => (
                  <UnmatchedRow
                    key={item.id}
                    item={item}
                    options={review.active_countries}
                    taken={taken}
                    canDecide={canDecide}
                    busy={decide.isPending}
                    onDecide={(decision, countryId) =>
                      decide.mutate({
                        itemId: item.id,
                        body: { decision, country_id: countryId ?? null, expected_version: item.version },
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="hint">A mapped spelling is remembered as an alias of that country when you accept.</p>
      </div>
    </div>
  );
}

function UnmatchedRow({
  item,
  options,
  taken,
  canDecide,
  busy,
  onDecide,
}: {
  item: BillableDaysCountryItem;
  options: BillableDaysReview['active_countries'];
  taken: Map<string, string>;
  canDecide: boolean;
  busy: boolean;
  onDecide: (decision: 'MAP' | 'EXCLUDE' | 'UNDECIDED', countryId?: string) => void;
}) {
  const [choice, setChoice] = useState(item.decided_country?.id ?? '');
  const undecided = item.decision.code === UNDECIDED;
  return (
    <tr>
      <td>
        <div className="us-eng-strong">{item.raw_country_name}</div>
        <div className="hint mono">{item.source_column_name}</div>
      </td>
      <td>
        <span className={decisionPill(item.decision.code)}>{item.decision.name}</span>
        {item.decided_country && <div className="hint">→ {item.decided_country.name}</div>}
      </td>
      <td>
        {canDecide && undecided ? (
          <select
            aria-label={`Map ${item.raw_country_name} to`}
            value={choice}
            onChange={(event) => setChoice(event.target.value)}
          >
            <option value="">Choose a country…</option>
            {options.map((country) => {
              const usedBy = taken.get(country.id);
              return (
                <option key={country.id} value={country.id} disabled={Boolean(usedBy && usedBy !== item.id)}>
                  {country.code} — {country.name}
                  {usedBy && usedBy !== item.id ? ' (already in the report)' : ''}
                </option>
              );
            })}
          </select>
        ) : (
          <span className="hint">{item.decided_country ? item.decided_country.code : '—'}</span>
        )}
      </td>
      <td>
        {canDecide && (
          <div className="row-actions">
            {undecided ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy || !choice}
                  onClick={() => onDecide('MAP', choice)}
                >
                  <Check aria-hidden="true" /> Map
                </button>
                <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                  onClick={() => onDecide('EXCLUDE')}>
                  <Ban aria-hidden="true" /> Exclude
                </button>
              </>
            ) : (
              <button type="button" className="icon-btn" title="Back to awaiting decision" aria-label="Undo decision"
                disabled={busy} onClick={() => onDecide('UNDECIDED')}>
                <RotateCcw aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
